import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, BookOpen, Heart, ShieldOff, Frown, Sparkles, Users } from 'lucide-react';

export default function FourBasicChoices() {
  const navigate = useNavigate();

  const choices = [
    {
      number: 1,
      title: 'HEAR BLAME, BLAME BACK',
      description: 'Defensive reaction - we blame the other person to protect ourselves',
      longDescription: 'When we feel attacked, our instinct may be to attack back. This creates a cycle of blame.',
      color: 'from-rose-950/40 to-red-900/30',
      borderColor: 'border-rose-700/40',
      icon: ShieldOff,
      iconColor: 'text-rose-400'
    },
    {
      number: 2,
      title: 'HEAR BLAME, BLAME SELF',
      description: 'Turn blame inward - we agree and feel guilt or shame',
      longDescription: 'Sometimes we absorb blame, believing we deserve it. This erodes our self-worth.',
      color: 'from-amber-950/40 to-orange-900/30',
      borderColor: 'border-amber-700/40',
      icon: Frown,
      iconColor: 'text-amber-400'
    },
    {
      number: 3,
      title: 'HEAR MY OWN FEELINGS & NEEDS',
      description: 'Connect with what\'s alive in me (Honesty)',
      longDescription: 'We pause to notice: What am I feeling? What do I need right now?',
      color: 'from-rose-900/30 to-pink-900/40',
      borderColor: 'border-rose-500/40',
      icon: Heart,
      iconColor: 'text-rose-400'
    },
    {
      number: 4,
      title: 'HEAR SPEAKER\'S FEELINGS & NEEDS',
      description: 'Connect with what\'s alive in them (Empathy)',
      longDescription: 'We listen deeply: What might they be feeling? What do they need?',
      color: 'from-teal-900/30 to-cyan-900/40',
      borderColor: 'border-teal-500/40',
      icon: Users,
      iconColor: 'text-teal-400'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Decorative background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-64 h-64 bg-rose-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl"></div>
      </div>

      {/* Header */}
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/belonging-ebook')} variant="outline" size="sm" className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Guide
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">Our 4 Basic Choices of Response</h1>
              </div>
            </div>
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <BookOpen className="h-4 w-4" />
              <span>Page 81</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-12 relative z-10">
        <div className="max-w-5xl mx-auto">
          {/* Hero Section with Image */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-rose-500/10 border border-rose-500/20 rounded-full text-rose-300 text-sm mb-6">
              <Sparkles className="h-4 w-4" />
              <span>Core NVC Concept</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              In Any Given Situation,<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-400 to-amber-400">We Have Four Choices</span>
            </h2>
            <p className="text-xl text-slate-300 leading-relaxed max-w-3xl mx-auto mb-8">
              When someone says something we find difficult, how we respond shapes everything that follows. 
              Two choices create disconnection; two create connection.
            </p>
            
            {/* Inspirational Quote */}
            <div className="bg-gradient-to-r from-slate-800/50 to-slate-700/30 border border-slate-600/30 rounded-2xl p-6 max-w-2xl mx-auto">
              <p className="text-lg text-slate-200 italic leading-relaxed">
                "Every message, regardless of its form or content, is an expression of a need."
              </p>
              <p className="text-rose-400 mt-3 font-medium">— Marshall Rosenberg</p>
            </div>
          </div>

          {/* Four Choices Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {choices.map((choice, idx) => {
              const IconComponent = choice.icon;
              return (
                <div key={idx} className={`bg-gradient-to-br ${choice.color} border ${choice.borderColor} rounded-2xl p-8 hover:scale-[1.02] transition-all duration-300 hover:shadow-lg hover:shadow-black/20 group`}>
                  <div className="flex items-start gap-4 mb-5">
                    <div className={`p-3 rounded-xl bg-black/20 ${choice.iconColor} group-hover:scale-110 transition-transform`}>
                      <IconComponent className="h-8 w-8" />
                    </div>
                    <div className="flex-1">
                      <div className="text-white/50 font-mono text-xs tracking-wider mb-1">CHOICE {choice.number}</div>
                      <h3 className="text-xl font-bold text-white leading-tight">{choice.title}</h3>
                    </div>
                  </div>
                  <p className="text-slate-200 leading-relaxed mb-3">{choice.description}</p>
                  <p className="text-slate-400 text-sm leading-relaxed italic">{choice.longDescription}</p>
                </div>
              );
            })}
          </div>

          {/* Connection vs Disconnection - Enhanced */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-rose-950/30 to-red-900/20 border border-rose-700/30 rounded-2xl p-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-rose-500/10 rounded-full blur-2xl"></div>
              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-rose-500/20 rounded-lg">
                    <ShieldOff className="h-5 w-5 text-rose-400" />
                  </div>
                  <h3 className="text-xl font-bold text-rose-200">Creates Disconnection</h3>
                </div>
                <div className="space-y-3 mb-4">
                  <div className="flex items-center gap-3 text-rose-100">
                    <span className="w-6 h-6 flex items-center justify-center bg-rose-500/20 rounded-full text-xs font-bold">1</span>
                    <span>Blame others — defense mode</span>
                  </div>
                  <div className="flex items-center gap-3 text-rose-100">
                    <span className="w-6 h-6 flex items-center justify-center bg-rose-500/20 rounded-full text-xs font-bold">2</span>
                    <span>Blame yourself — shame mode</span>
                  </div>
                </div>
                <p className="text-rose-200/70 text-sm italic border-t border-rose-500/20 pt-4">
                  These choices keep us stuck in judgment, shame, and cycles of conflict
                </p>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-teal-950/30 to-emerald-900/20 border border-teal-700/30 rounded-2xl p-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-teal-500/10 rounded-full blur-2xl"></div>
              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-teal-500/20 rounded-lg">
                    <Heart className="h-5 w-5 text-teal-400" />
                  </div>
                  <h3 className="text-xl font-bold text-teal-200">Creates Connection</h3>
                </div>
                <div className="space-y-3 mb-4">
                  <div className="flex items-center gap-3 text-teal-100">
                    <span className="w-6 h-6 flex items-center justify-center bg-teal-500/20 rounded-full text-xs font-bold">3</span>
                    <span>Connect with your feelings & needs</span>
                  </div>
                  <div className="flex items-center gap-3 text-teal-100">
                    <span className="w-6 h-6 flex items-center justify-center bg-teal-500/20 rounded-full text-xs font-bold">4</span>
                    <span>Connect with their feelings & needs</span>
                  </div>
                </div>
                <p className="text-teal-200/70 text-sm italic border-t border-teal-500/20 pt-4">
                  These choices open dialogue, deepen understanding, and nurture relationships
                </p>
              </div>
            </div>
          </div>

          {/* Warm Closing Image Section */}
          <div className="mt-16 text-center">
            <div className="bg-gradient-to-r from-slate-800/30 to-slate-700/20 border border-slate-600/20 rounded-2xl p-8">
              <Heart className="h-12 w-12 text-rose-400 mx-auto mb-4" />
              <p className="text-xl text-slate-200 leading-relaxed max-w-2xl mx-auto">
                Remember: The goal isn't to be perfect. It's to become more aware of our choices, 
                so we can choose connection more often.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
